import React, { useState, useEffect } from 'react';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import CommonTable from '../../components/commonComponent/CommanTable';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError, confirmDelete } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa';

const StatePage = () => {
  const [states, setStates] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formValues, setFormValues] = useState({
    name: '',
    country: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [countries, setCountries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');  
  
  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const res = await axios.get('/admin/api/country');
      setCountries(res.data.countries || []);
    } catch (err) {
      toastError('Failed to fetch countries');
    }
  };

  const fetchStates = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/admin/api/state');
      setStates(res.data.states || []);
    } catch (err) {
      console.error('Failed to fetch States:', err);
      toastError('Failed to fetch states');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const validationErrors = validateForm(formValues, {
      name: { required: true },
      country: { required: true }, // Require country
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: formValues.name,
        country: formValues.country, // Add country to payload
      };

      if (editingId) {
        await axios.put(`/admin/api/state/${editingId}`, payload);
        toastSuccess('State updated successfully!');
      } else {
        await axios.post('/admin/api/state', payload);
        toastSuccess('State added successfully!');
      }
      fetchStates();
      handleCancel();
    } catch (err) {
      console.error('Failed to save State:', err);
      toastError(err.response?.data?.message || 'Failed to save State');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (state) => {
    setFormValues({
      name: state.name || '',
      country: state.country?._id || '', // Set country for edit
    });
    setEditingId(state._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      const res = await confirmDelete('Are you sure you want to delete this state?');
      if (!res.isConfirmed) return;
    } catch (err) { return; }
    try {
      await axios.delete(`/admin/api/state/${id}`);
      toastSuccess('State deleted successfully!');
      fetchStates();
    } catch (err) {
      console.error('Failed to delete State:', err);
      toastError('Failed to delete State');
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({ name: '', country: '' });
    setEditingId(null);
    setErrors({});
  };

  // Define table columns for CommonTable
  const tableColumns = [
    { key: 'srNo', label: 'Sr no.' },
    { key: 'name', label: 'Name' },
    { key: 'actions', label: 'Actions' }
  ];

  // Prepare data for CommonTable
  const filteredStates = states.filter(state => 
    state.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const tableData = filteredStates.map((state, idx) => ({
    srNo: idx + 1,
    name: state.name,
    actions: (
      <div>
        <button
          className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0 text-primary-custom"
          title="Edit"
          onClick={() => handleEdit(state)}
        >
          <FaEdit size={14} />
        </button>
        <button
          className="btn btn-sm py-0 px-2 fs-6 lh-sm shadow-none border-0 text-danger"
          title="Delete"
          onClick={() => handleDelete(state._id)}
        >
          <FaTrash size={14} />
        </button>
      </div>
    )
  }));

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0 mt-4">State</h3>
        <div className="d-flex gap-2">
          <input
            type="text"
            className="form-control"
            placeholder="Search States..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: '250px' }}
          />
          <button
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
          >
            + Add New State
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card">
          <div className="card-body">
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <CommonTable
          columns={tableColumns}
          data={tableData}
        />
      )}
      <CommonModalForm
        visible={showModal}
        title={editingId ? 'Edit State' : 'Add State'}
        fields={[
           {
            name: 'country',
            label: 'Country',
            type: 'select',
            options: countries.map(c => ({ value: c._id, label: c.name })),
            placeholder: 'Select Country'
          },
          { 
            name: 'name', 
            label: 'State Name', 
            type: 'text',
            placeholder: 'Enter State Name'
          }
        ]}
        values={formValues}
        errors={errors}
        onChange={setFormValues}
        onSubmit={handleSave}
        onCancel={handleCancel}
        submitLabel={saving ? (editingId ? 'Updating...' : 'Saving...') : (editingId ? 'Update' : 'Save')}
        requiredFields={['name', 'country']}
        disabled={saving}
      />
    </div>
  );
};

export default StatePage;
